import { useLayoutEffect } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/**
 * Resets scroll to the top on client-side navigation.
 *
 * The browser only restores scroll for real document loads; a react-router
 * navigation just swaps the tree and leaves the viewport where it was. That was
 * visible on the landing page's footer "Contact Us" link — you'd arrive on
 * /contact already scrolled to the bottom.
 *
 * Two deliberate exceptions:
 *
 * - **A hash target.** `/#engage` means "go to that section", so scrolling to
 *   the top would fight the anchor. Left to the browser.
 * - **POP navigations** (back/forward). The browser restores the previous scroll
 *   offset there, and overriding it makes going back feel broken.
 *
 * useLayoutEffect rather than useEffect so the jump happens before paint — with
 * useEffect the old offset is briefly visible on the new page.
 */
const ScrollToTop: React.FC = () => {
  const { pathname, hash } = useLocation();
  const navigationType = useNavigationType();

  useLayoutEffect(() => {
    if (hash !== '' || navigationType === 'POP') {
      return;
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname, hash, navigationType]);

  return null;
};

export default ScrollToTop;
